/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import _ from "lodash";
import { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { Edit, Trash } from "@jda/lui-common-icon-library-mui5";
import { LuiThreeDotMenu, LuiThreeDotMenuThumbnailType } from "@jda/lui-common-component-library-mui5";

import { useStyles } from "./styles";
import Loader from "../../shared/loader/Loader";
import RealmDetails from "../realmDetails/RealmDetails";
import SearchBar from "../../shared/searchLists/SearchBar";
import SearchList from "../../shared/searchLists/SearchList";
import AddRealmDialog from "../addRealmDialog/AddRealmDialog";
import { getRealms, removeRealm } from "../../store/realm/realm";
import { MESSAGES, PAGE_SIZE_REALMS, TITLES } from "./constants";
import { actions, resources } from "../checkPermission/constants";
import SearchListItem from "../../shared/searchLists/SearchListItem";
import DeleteRealmDialog from "../deleteRealmDialog/DeleteRealmDialog";
import EmptyComponent from "../../shared/emptyComponent/EmptyComponent";
import { getEnvTypesList } from "../../store/stystemSpec/stystemSpec";
import { IDeleteRealmRequest } from "../../models/Realms/RealmContracts";
import { hideLoaderAction } from "../../store/loader/loaderActionCreator";
import { ThreeDotOption } from "../../shared/ThreeDotOption/ThreeDotOption";
import { selectAllAssignedPermissions } from "../../store/auth/authSelector";
import { setSelectedRealmAction } from "../../store/realm/realmActionCreator";
import { selectSelectedCustomer } from "../../store/customer/customerSelector";
import useWindowDimensions from "../../shared/windowDimensions/windowDimensions";
import { selectSelectedSubEnterprise } from "../../store/subEnterprise/subEnterpriseSelector";
import { checkAuthzPermission, CheckAuthzPermissions } from "../checkPermission/CheckViewPermission";
import { selectAllRealms, selectRealmPagination, selectSelectedRealm } from "../../store/realm/realmSelector";

const Realms = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { height } = useWindowDimensions();

  const selectRealms = useSelector(selectAllRealms);
  const selectedRealm = useSelector(selectSelectedRealm);
  const selectedCustomer = useSelector(selectSelectedCustomer);
  const selectedRealmPagination = useSelector(selectRealmPagination);
  const assignedPermissions = useSelector(selectAllAssignedPermissions);
  const selectedSubEnterprise = useSelector(selectSelectedSubEnterprise);

  const addRealmPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.CREATE, assignedPermissions.resources[resources.REALM]);
  const editRealmPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.EDIT, assignedPermissions.resources[resources.REALM]);
  const deleteRealmPermission =
    assignedPermissions &&
    assignedPermissions.resources &&
    checkAuthzPermission(actions.DELETE, assignedPermissions.resources[resources.REALM]);

  const [action, SetAction] = useState("");
  const [searchString, setSearchString] = useState("");

  useEffect(() => {
    getAllRealms();
  }, [selectedSubEnterprise?.id]);

  const getAllRealms = (pageNo?: number) => {
    dispatch(setSelectedRealmAction({}));
    if (selectedCustomer?.id && selectedSubEnterprise?.id) {
      const realmPayload = {
        pageSize: PAGE_SIZE_REALMS,
        searchString: '',
        pageNo: pageNo || 1,
        customerId: selectedCustomer.id,
        subEnterpriseId: selectedSubEnterprise.id,
      }
      getRealms(dispatch, realmPayload);
    };
    getEnvTypesList(dispatch);
  };

  const onSearchTextBoxChange = async (searchValue: string) => {
    setSearchString(searchValue);
    if (searchValue.trim() !== "" && searchValue.length > 2) {
      const realmPayload = {
        pageNo: 1,
        pageSize: PAGE_SIZE_REALMS,
        customerId: selectedCustomer.id,
        searchString: searchValue.trim(),
        subEnterpriseId: selectedSubEnterprise.id,
      }
      getRealms(dispatch, realmPayload);
    } else {
    }
    dispatch(hideLoaderAction(MESSAGES.REALM_TITLE));
  };

  const handleListItemClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, listItem: any) => {
    event 
    const target = event.target as HTMLElement;
    if (target?.dataset?.testid === "three-dot-menu-icon" || target?.dataset?.testid === "three-dot-menu-icon-button") {
      return;
    }
    dispatch(setSelectedRealmAction(listItem));
  };

  const handleMenuClick = (actionName: string) => {
    SetAction(actionName);
  };

  const handleCloseDialog = () => {
    SetAction("");
  };

  const handleDeleteRealm = async () => {
    const payload: IDeleteRealmRequest = {
      id: selectedRealm.id,
      customerId: selectedCustomer.id,
      subEnterpriseId: selectedSubEnterprise.id,
    }
    await removeRealm(dispatch, payload);
    handleCloseDialog();
  };

  const items: LuiThreeDotMenuThumbnailType[] = [
    editRealmPermission && {
      render: <ThreeDotOption icon={<Edit fontSize="small" />} text={TITLES.EDIT} />,
      onClick: () => handleMenuClick("Edit"),
      autoClose: true,
    },
    deleteRealmPermission && {
      render: <ThreeDotOption icon={<Trash fontSize="small" />} text={TITLES.DELETE} />,
      onClick: () => handleMenuClick("Delete"),
      autoClose: true,
    },
  ];

  const showLoader = false;

  return (
    <Box>
      <Box className={selectedSubEnterprise.id === '' ? classes.hide : classes.realmsWrap}>
        <Box>
          <Box className={(selectRealms && selectRealms.length > 0) || (searchString.length) ? classes.root : classes.hide}>
            <Box className={classes.topLeftHeaderContainer}>
              <SearchBar
                placeHolder={TITLES.REALM_NAME}
                onClearTextInput={getAllRealms}
                addButtonDialog={handleMenuClick}
                showAddButton={addRealmPermission}
                addButtonMessage={TITLES.ADD_REALM}
                onSearchTextBoxChange={onSearchTextBoxChange}
              />
            </Box>
          </Box>
          <CheckAuthzPermissions action={actions.VIEW} resource={resources.REALM}>
            <Box component="div" className={classes.listItemPaper}>
              <Loader isCenter showLoader={showLoader} customClasses={classes.loader} />
              {selectRealms.length ?
                <SearchList
                  dense={false}
                  minHeight={70}
                  addDivider={true}
                  key={"RealmList"}
                  data={selectRealms}
                  height={height - 330}
                  disableGutters={false}
                  selectedItem={selectedRealm}
                  onClick={handleListItemClick}
                  listStyle={classes.searchListContainer}
                  totalCount={selectedRealmPagination.totalCount}
                  currentPage={selectedRealmPagination.pageNumber}
                  onPageChange={(pageNo: number) => {
                    console.log("Page No:", pageNo);
                    // getAllRealms(pageNo);
                  }}
                  renderItem={(item) => (
                    <SearchListItem
                      key={item?.id}
                      showAvatarContent={true}
                      ListPrimaryContent={
                        <Box>
                          <Typography variant="body1" className={classes.textEllipsisPrimary}>
                            <Box component="span" data-testid="name" fontWeight="fontWeightBold" title={item?.realmName}>
                              {item?.realmName}
                            </Box>
                          </Typography>
                        </Box>
                      }
                      ListSecondaryContent={
                        <Box>
                          <Typography variant="body1" color="textSecondary" className={classes.textEllipsisSecondary}>
                            <Box
                              component="span"
                              data-testid="realm-id"
                              fontWeight="fontWeightNormal"
                              title={item?.displayName}
                            >
                              {item?.displayName}
                            </Box>
                          </Typography>
                        </Box>
                      }
                      ListSecondaryActionContent={
                        editRealmPermission || deleteRealmPermission ?

                            <LuiThreeDotMenu dataTestId="three-dot-menu" items={_.compact(items)} ariaLabel="Three Dot Menu" />
                           : null
                      }
                    />
                  )}
                /> : null
              }
              {searchString.length > 0 && selectRealms.length === 0 &&
                <EmptyComponent
                  disableCreateAction
                  subTitle={MESSAGES.SELECT_REALM}
                  title={MESSAGES.NO_RESULTS_FOUND}
                />
              }
            </Box>
          </CheckAuthzPermissions>
        </Box>
        {selectedRealm.id &&
          <Box className={classes.rightContainer}>
            <RealmDetails />
          </Box>
        }
        {(selectRealms && selectRealms.length === 0) && searchString === "" &&
          <Box className={classes.rightContainer}>
            <EmptyComponent
              subTitle=""
              disableCreateAction={false}
              buttonText={MESSAGES.REALM_ADD_BUTTON}
              title={MESSAGES.REALM_EMPTY_SCREEN_TITLE}
              handleOnCreateAction={() => handleMenuClick("Add")}
            />
          </Box>
        }
        <AddRealmDialog
          realmData={selectedRealm}
          isEdit={action === "Edit"}
          onClose={handleCloseDialog}
          openModal={action === "Edit" || action === "Add"}
        />
        <DeleteRealmDialog
          realmData={selectedRealm}
          onClose={handleCloseDialog}
          deleteRealm={handleDeleteRealm}
          openModal={action === "Delete"}
        />
      </Box>
    </Box>
  );
};

export default Realms;
